import React from 'react';
import { connect } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { Translate, I18n } from 'react-redux-i18n';
import { MemberDTO } from '../../../../../network/api/types/compte/MemberDTO';
import Modal from '../../../../common/modals/Modal';
import TextInput from '../../../../common/inputs/TextInput';
import Button from '../../../../common/Button';
import { FieldErrorDTO } from '../../../../../network/api/types/compte/FieldErrorDTO';
import { validateEmail, validatePhone } from '../../../../../services/utils';
import ChipsListAutocomplete from '../../../../common/chips/ChipsListAutocomplete';
import { StatutEnum } from '../../../../../network/api/types/compte/CompteProfilProProfileDetails';
import { MonIdentite } from '../../../../../network/api/types/compte/MonIdentite';

type Props = {
  photo: string
  member: MemberDTO
  isOpen: boolean
  openModal: () => void
  selectedDomaines: string[]
  domaines: string[]
  handleEdit: (values: FormValues) => void
  myIdentity?: MonIdentite
};

export type FormValues = {
  domaines?: string[]
  domainesSelect?: string
  fonction?: string
  email?: string
  telephoneFixe?: string
  telephoneMobile?: string
  statut?: StatutEnum
};

type State = {
  domainesDirty: boolean
};

class CollaboratorsModifyModal extends React.Component<Props, State> {
  state: State = {
    domainesDirty: false,
  };

  getErrorMessage = (errors: FieldErrorDTO[], fieldName: string): string => {
    if (errors) {
      return errors.find((error: FieldErrorDTO) => error.field === fieldName)?.message;
    }
    return '';
  };

  render(): React.ReactNode {
    if (!this.props.isOpen) {
      return false;
    }
    const member = this.props.member;
    const initialValues: FormValues = {
      fonction: member.fonction,
      email: member.email,
      telephoneFixe: member.telephoneFixe,
      telephoneMobile: member.telephoneMobile,
      statut: member.statut,
    };
    return (
      <Modal
        isOpen={this.props.isOpen}
        onClose={(e) => {
          e.stopPropagation();
          this.props.openModal();
        }}
      >
        <div className="collab-form-title">
          <div className="collab-photo-container">
            <img
              src={this.props.photo}
              alt="photoz"
            />
          </div>
          <div className="collab-fullname">
            <span>{this.props.member.prenom}</span>
            <span>{this.props.member.nomUsuel}</span>
          </div>
        </div>
        <Form
          onSubmit={this.props.handleEdit}
          initialValues={initialValues}
          render={({
            handleSubmit, form, submitError,
          }) => {
            const resetAutocompleteField = (field: 'lang' | 'domaine') =>
              form.change(`${field}sSelect` as keyof FormValues, '');
            return (
              <form
                className="collab-info-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (this.state.domainesDirty || form.getState().dirty) {
                    handleSubmit(e);
                  } else {
                    this.props.openModal();
                  }
                }}
              >
                <Field
                  name="fonction"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.FONCTION')}
                  type="text"
                  readOnly
                />
                {this.props.member.fonction.toLowerCase().includes('collaborateur') && (
                  <>
                    <Translate className={`subtitle${(initialValues.statut === null) ? ' danger' : ''}`} value="COLLABORATORS_INFORMATION.STATUT" />
                    <div className="big-button-radio-container">
                      <Field
                        type="radio"
                        name="statut"
                        value={StatutEnum.SALARIE}
                        render={({ input }) => (
                          <label
                            htmlFor="salarie"
                            className={`big-button-radio${(initialValues.statut === null && !input.checked) ? ' danger' : ''}${this.props.myIdentity.idn === this.props.member.idn ? ' read-only' : ''}`}
                          >
                            <input
                              type="radio"
                              name={input.name}
                              id="salarie"
                              value={StatutEnum.SALARIE}
                              checked={input.checked}
                              onChange={input.onChange}
                              disabled={this.props.myIdentity.idn === this.props.member.idn}
                            />
                            <span className="button-customize" />
                            <Translate value="COLLABORATORS_INFORMATION.SALARIE" />
                          </label>
                        )}
                      />
                      <Field
                        type="radio"
                        name="statut"
                        value={StatutEnum.NONSALARIE}
                        render={({ input }) => (
                          <label
                            htmlFor="nonsalarie"
                            className={`big-button-radio${(initialValues.statut === null && !input.checked) ? ' danger' : ''}${this.props.myIdentity.idn === this.props.member.idn ? ' read-only' : ''}`}
                          >
                            <input
                              type="radio"
                              name={input.name}
                              id="nonsalarie"
                              value={StatutEnum.NONSALARIE}
                              checked={input.checked}
                              onChange={input.onChange}
                              disabled={this.props.myIdentity.idn === this.props.member.idn}
                            />
                            <span className="button-customize" />
                            <Translate value="COLLABORATORS_INFORMATION.NONSALARIE" />
                          </label>
                        )}
                      />
                    </div>
                  </>
                )}
                <ChipsListAutocomplete
                  type="domaine"
                  setDirty={() => this.setState({ domainesDirty: true })}
                  resetField={() => resetAutocompleteField('domaine')}
                  className="domaines-autocomplete"
                  subtitleI18n="COLLABORATORS_INFORMATION.DOMAIN"
                  placeholderI18n="SEARCH.DOMAINES"
                  allElements={this.props.domaines.map((domaine) => ({
                    id: domaine,
                    label: domaine,
                  }))}
                  selectedElements={this.props.selectedDomaines}
                  updateField={(domaines) => form.change('domaines', domaines)}
                />
                <Field
                  name="email"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.MAIL_PRO')}
                  type="text"
                  customError={this.getErrorMessage(submitError, 'email')}
                  validate={validateEmail}
                />
                <Field
                  name="telephoneFixe"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.TEL_FIXE')}
                  type="text"
                  customError={this.getErrorMessage(submitError, 'telephoneFixe')}
                  validate={validatePhone}
                />
                <Field
                  name="telephoneMobile"
                  component={TextInput}
                  placeholder={I18n.t('COLLABORATORS_INFORMATION.TEL_MOBILE')}
                  type="text"
                  customError={this.getErrorMessage(submitError, 'telephoneMobile')}
                  validate={validatePhone}
                />
                <div className="button-container">
                  <Button
                    type="third"
                    onClick={form.getState().dirty ? form.reset : this.props.openModal}
                    i18nKey="actions.CANCEL"
                  />
                  <Button
                    type="primary"
                    buttonType="submit"
                    i18nKey="actions.SAVE"
                  />
                </div>
              </form>
            );
          }}
        />
      </Modal>
    );
  }
}

export default connect((state) => ({
  myIdentity: state.user.myIdentity,
}), {
})(CollaboratorsModifyModal);
